import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CubeGrid } from '../ui/CountingCubes';
import { QUESTIONS, ENCOURAGEMENTS } from '../../data/curriculum';
import { Check, X } from 'lucide-react';

const MatchNumberToGroup = ({ exercise, onAnswer }) => {
  const { number, options } = exercise;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  }, [exercise]);

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    const correct = answer === number;
    setIsCorrect(correct);
    setShowFeedback(true);

    setTimeout(() => {
      onAnswer(correct);
    }, 1200);
  };

  const getOptionClass = (option) => {
    const baseClass = 'p-4 rounded-2xl border-3 transition-all duration-200 cursor-pointer';
    
    if (!showFeedback) {
      return `${baseClass} bg-card border-border hover:border-primary hover:scale-105 active:scale-95`;
    }
    if (option === number) {
      return `${baseClass} bg-correct-light border-correct`;
    }
    if (option === selectedAnswer && !isCorrect) {
      return `${baseClass} bg-incorrect-light border-incorrect animate-shake`;
    }
    return `${baseClass} bg-card border-border opacity-50`;
  };

  const feedbackMessage = isCorrect 
    ? ENCOURAGEMENTS.correct[Math.floor(Math.random() * ENCOURAGEMENTS.correct.length)]
    : ENCOURAGEMENTS.incorrect[Math.floor(Math.random() * ENCOURAGEMENTS.incorrect.length)];

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
      {/* Question */}
      <motion.h2 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-display font-bold text-foreground mb-6 text-center"
      >
        {QUESTIONS['match-number-to-group']}
      </motion.h2>

      {/* Number Display */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="mb-8 w-20 h-20 rounded-2xl bg-primary flex items-center justify-center shadow-button"
      >
        <span className="text-4xl font-display font-bold text-primary-foreground">
          {number}
        </span>
      </motion.div>

      {/* Cube Group Options */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex gap-4 justify-center flex-wrap"
      >
        {options.map((option, index) => (
          <motion.button
            key={option}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1, type: 'spring' }}
            onClick={() => handleAnswer(option)}
            disabled={showFeedback}
            className={`${getOptionClass(option)} relative`}
            style={{ minWidth: '80px', minHeight: '80px' }}
          >
            {showFeedback && option === number && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-correct rounded-full flex items-center justify-center z-10"
              >
                <Check className="w-4 h-4 text-card" />
              </motion.span>
            )}
            {showFeedback && option === selectedAnswer && !isCorrect && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-incorrect rounded-full flex items-center justify-center z-10"
              >
                <X className="w-4 h-4 text-card" />
              </motion.span>
            )}
            <CubeGrid count={option} size="sm" animate={false} />
          </motion.button>
        ))}
      </motion.div>

      {/* Feedback */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-8 px-6 py-3 rounded-full font-display font-bold text-lg ${
              isCorrect 
                ? 'bg-correct-light text-success' 
                : 'bg-incorrect-light text-destructive'
            }`}
          >
            {feedbackMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MatchNumberToGroup;
